import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getAgentEvaluationSummary, getAgentTrackDisplayLabel, getAgentTrackStatus, isAgentTrack, isAgentTrackRunning } from "./agentTrackUtils";
import { getIsoDay } from "./flightTimingUtils";

dayjs.extend(utc);

/**
 * trackUtils — ScheduleDraft 轨 key 与航班集合的稳定工具层。
 *
 * 本模块把后端 ScheduleDraftTrack、外部参考轨和航班数组归一化逻辑集中在 lib 层，
 * 避免 canvas、workspace 与 hook 层重复拼接字符串 key。它只处理纯数据结构，
 * 不感知 React 状态或后端请求边界。
 *
 * 设计文档 → docs/20_workspace_ux/20_multitrack_view.md
 * 骨架关联 → lib/canvas/buildScheduleDraftCanvasModel.js (轨投影), lib/workspace/buildScheduleDraftWorkspaceModels.js (源轨选项), hooks/useScheduleDraftTrackConfig.js (轨配置状态)
 */

export const TRACK_KEY_PREFIX = "track";
export const EXTERNAL_TRACK_KEY_PREFIX = "external";
export const DRAFT_CURRENT_TRACK_KEY = "draft-current";
export const EDITING_TRACK_TYPE = "EDITING";
export const HISTORY_TRACK_TYPE = "HISTORY";
export const AGENT_TRACK_TYPE = "AGENT";
export const REFERENCE_TRACK_TYPE = "REFERENCE";
export const LIVE_REFERENCE_TRACK_TYPE = "LIVE_REFERENCE";
export const EXECUTION_SOURCE_TYPE = "EXECUTION";
const DELETED_CHANGE_STATUS = "DELETED";

const TRACK_TYPE_LABELS = {
    [EDITING_TRACK_TYPE]: "编辑轨",
    [HISTORY_TRACK_TYPE]: "历史快照",
    [AGENT_TRACK_TYPE]: "AI 方案轨",
    [REFERENCE_TRACK_TYPE]: "只读参考轨",
    [LIVE_REFERENCE_TRACK_TYPE]: "执行基线",
    EXECUTION_BASELINE_SNAPSHOT: "执行基线快照",
};

const REFERENCE_SOURCE_TYPE_LABELS = {
    BLANK: "空白创建",
    [EXECUTION_SOURCE_TYPE]: "执行航班",
    LIVE_EXECUTION: "实时执行航班",
    V1_SCHEDULE: "V1 计划",
    TRACK: "V2 历史内容",
    EXCEL_FILE: "Excel 文件",
    PASTED_TABLE: "粘贴表格",
    MANUAL: "手工录入",
};

export function formatTrackTypeLabel(trackType, fallback = "轨道") {
    const normalizedType = String(trackType || "").toUpperCase();
    return TRACK_TYPE_LABELS[normalizedType] || fallback;
}

export function formatReferenceSourceTypeLabel(sourceType, fallback = "参考来源") {
    const normalizedType = String(sourceType || "").toUpperCase();
    return REFERENCE_SOURCE_TYPE_LABELS[normalizedType] || fallback;
}

/**
 * 将未知输入收敛为数组，供轨与航班集合遍历使用。
 * @param {*} value - 可能为数组或空值的输入。
 * @returns {Array} 数组输入原样返回，其他值返回空数组。
 */
export function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function isDeletedFlight(flight) {
    return flight?.is_deleted || String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

export function getTrackFlights(track) {
    return toArray(track?.flights || track?.schedule_draft_flights).filter((flight) => !isDeletedFlight(flight));
}

export function getDraftTracks(draft) {
    return toArray(draft?.tracks);
}

export function getEditingTracks(draft) {
    return getDraftTracks(draft).filter((track) => track?.track_type === EDITING_TRACK_TYPE || track?.is_editable);
}

export function getHistoryTracks(draft) {
    return getDraftTracks(draft).filter((track) => track?.track_type === HISTORY_TRACK_TYPE);
}

export function getReferenceDisplayTracks(draft) {
    return getDraftTracks(draft).filter((track) => [HISTORY_TRACK_TYPE, AGENT_TRACK_TYPE, REFERENCE_TRACK_TYPE, LIVE_REFERENCE_TRACK_TYPE].includes(track?.track_type));
}

function getTrackType(track) {
    return String(track?.track_type || "").toUpperCase();
}

export function getWritableEditingTracks(draft) {
    return getDraftTracks(draft).filter((track) => getTrackType(track) === EDITING_TRACK_TYPE && track?.is_editable !== false && !track?.finalized_at);
}

function getComparisonBaselineConsumers(draft, track) {
    if (!track?.id) {
        return [];
    }
    return getDraftTracks(draft).filter((candidate) => (
        getTrackType(candidate) === EDITING_TRACK_TYPE
        && String(candidate?.compared_reference_track_id || "") === String(track.id)
    ));
}

function getTrackDeleteActionLabel(track) {
    const trackType = getTrackType(track);
    if ([REFERENCE_TRACK_TYPE, LIVE_REFERENCE_TRACK_TYPE].includes(trackType)) {
        return "移除参考轨";
    }
    if (trackType === AGENT_TRACK_TYPE) {
        return "删除方案轨";
    }
    return "删除轨";
}

function getTrackDeleteConfirmContent(track) {
    const trackType = getTrackType(track);
    if ([REFERENCE_TRACK_TYPE, LIVE_REFERENCE_TRACK_TYPE].includes(trackType)) {
        return "只移除当前 Draft 中的参考轨记录及其展示，不会删除外部来源或执行航班数据。";
    }
    if (trackType === HISTORY_TRACK_TYPE) {
        return "将删除这条历史快照轨及其下的快照航班记录，此操作无法恢复。";
    }
    if (trackType === AGENT_TRACK_TYPE) {
        return "将删除这条 Agent 方案轨及其下航班记录；正在运行的方案不会允许删除。";
    }
    return "将删除这条编辑轨及其下所有航班记录，此操作无法恢复。";
}

export function buildTrackDeleteAction(draft, track, options = {}) {
    if (!track?.id) {
        return null;
    }

    const trackType = getTrackType(track);
    const label = getTrackDeleteActionLabel(track);
    const disabledReasons = [];
    const baselineConsumers = getComparisonBaselineConsumers(draft, track);

    if (!options.canDeleteTrack) {
        disabledReasons.push("缺少 schedule:delete 权限");
    }
    if (options.isAgentWorking) {
        disabledReasons.push("Agent 执行期间不可删除轨道");
    }
    if (trackType === EDITING_TRACK_TYPE) {
        if (track.is_editable === false || track.finalized_at) {
            disabledReasons.push("已发布或只读的编辑轨不能删除");
        } else if (getWritableEditingTracks(draft).length <= 1) {
            disabledReasons.push("不能删除唯一可编辑编辑轨");
        }
    }
    if (isAgentTrack(track) && isAgentTrackRunning(track)) {
        disabledReasons.push(`Agent 方案仍在执行中（${getAgentTrackStatus(track) || "RUNNING"}）`);
    }
    if (baselineConsumers.length) {
        const consumerNames = baselineConsumers.map((consumer) => consumer.name || "未命名编辑轨").join("、");
        disabledReasons.push(`已被 ${consumerNames} 作为 Compare 执行基线绑定`);
    }

    const trackName = getTrackDisplayName(track);
    return {
        type: "delete-track",
        trackId: String(track.id),
        track,
        label,
        disabled: disabledReasons.length > 0,
        disabledReason: disabledReasons[0] || "",
        disabledReasons,
        confirmTitle: `${label}：${trackName}`,
        confirmContent: getTrackDeleteConfirmContent(track),
        okText: label,
        danger: true,
    };
}

function getSourceParams(referenceSource) {
    const metricsSource = referenceSource?.metrics ? referenceSource : referenceSource?.track;
    const metrics = metricsSource?.metrics && typeof metricsSource.metrics === "object" ? metricsSource.metrics : {};
    return metrics.source_params && typeof metrics.source_params === "object" ? metrics.source_params : {};
}

export function getReferenceSourceType(referenceSource) {
    const sourceParams = getSourceParams(referenceSource);
    const sourceType = referenceSource?.source_type
        ?? referenceSource?.sourceType
        ?? referenceSource?.track?.source_type
        ?? referenceSource?.track?.sourceType
        ?? sourceParams.source_type;
    return sourceType ? String(sourceType).toUpperCase() : "";
}

export function isExecutionReferenceSource(referenceSource) {
    const trackType = referenceSource?.track_type ?? referenceSource?.track?.track_type;
    return trackType === LIVE_REFERENCE_TRACK_TYPE || getReferenceSourceType(referenceSource) === EXECUTION_SOURCE_TYPE;
}

export function getDefaultEditingTrack(draft) {
    const editingTracks = getEditingTracks(draft);
    if (!editingTracks.length) {
        return null;
    }
    return editingTracks.find((track) => String(track.id) === String(draft?.default_track_id)) || editingTracks[0];
}

export function getDraftFlights(draft) {
    const editingTrackFlights = getEditingTracks(draft).flatMap(getTrackFlights);
    return editingTrackFlights.length ? editingTrackFlights : toArray(draft?.flights).filter((flight) => !isDeletedFlight(flight));
}

export function getTrackFlightCount(track) {
    const metricsCount = track?.metrics?.flight_count ?? track?.evaluation?.flight_count;
    return metricsCount ?? getTrackFlights(track).length;
}

export function getReferenceDateShiftTarget(referenceSource) {
    return referenceSource?.metrics?.date_shift_target || referenceSource?.metrics?.source_params?.date_shift_target || referenceSource?.agent_context?.date_shift_target || referenceSource?.date_shift_target;
}

function getSourceFlightDate(flight) {
    const value = flight?.flight_date || flight?.std;
    const parsed = value ? dayjs.utc(value) : null;
    return parsed?.isValid() ? parsed.startOf("day") : null;
}

// 日期平移锚点：优先来源查询窗口起始日。来源查询是区间重叠语义，窗口首日前起飞、
// 首日到达的过夜班会把"最早航班日期"拉早一天，导致"平移到窗口起始日"被算成 +1 天。
function getReferenceWindowStartDate(referenceSource) {
    const value = referenceSource?.metrics?.source_params?.start_date
        ?? referenceSource?.source_params?.start_date;
    const parsed = value ? dayjs.utc(value) : null;
    return parsed?.isValid() ? parsed.startOf("day") : null;
}

function getEarliestSourceFlightDate(flights) {
    const sourceDates = toArray(flights).map(getSourceFlightDate).filter(Boolean);
    if (!sourceDates.length) {
        return null;
    }
    return sourceDates.reduce((earliest, current) => (current.isBefore(earliest) ? current : earliest), sourceDates[0]);
}

function shiftDateValue(value, offsetDays, format) {
    if (!value) {
        return value;
    }

    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) {
        return value;
    }

    return parsed.add(offsetDays, "day").format(format);
}

function shiftDateTimeValue(value, offsetDays) {
    if (!value) {
        return value;
    }

    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) {
        return value;
    }

    const shifted = parsed.add(offsetDays, "day");
    return shifted.toISOString();
}

export function applyReferenceDateShift(flights = [], referenceSource) {
    const dateShiftTarget = getReferenceDateShiftTarget(referenceSource);
    if (!dateShiftTarget) {
        return toArray(flights);
    }

    const targetDate = dayjs.utc(dateShiftTarget).startOf("day");
    if (!targetDate.isValid()) {
        return toArray(flights);
    }

    const anchorDate = getReferenceWindowStartDate(referenceSource) || getEarliestSourceFlightDate(flights);
    if (!anchorDate) {
        return toArray(flights);
    }

    const offsetDays = targetDate.diff(anchorDate, "day");
    if (!offsetDays) {
        return toArray(flights);
    }

    return toArray(flights).map((flight) => {
        const shiftedFlightDate = shiftDateValue(flight.flight_date || flight.std, offsetDays, "YYYY-MM-DD");
        const shiftedDate = shiftedFlightDate ? dayjs.utc(shiftedFlightDate) : null;
        return {
            ...flight,
            std: shiftDateTimeValue(flight.std, offsetDays),
            sta: shiftDateTimeValue(flight.sta, offsetDays),
            etd: shiftDateTimeValue(flight.etd, offsetDays),
            eta: shiftDateTimeValue(flight.eta, offsetDays),
            effective_departure: shiftDateTimeValue(flight.effective_departure, offsetDays),
            effective_arrival: shiftDateTimeValue(flight.effective_arrival, offsetDays),
            flight_date: shiftedFlightDate || flight.flight_date,
            day_of_week: shiftedDate?.isValid() ? getIsoDay(shiftedDate) : flight.day_of_week,
            date_shift_offset_days: offsetDays,
        };
    });
}

export function getTrackDisplayName(track) {
    if (isAgentTrack(track)) {
        return `AI 方案 · ${getAgentTrackDisplayLabel(track)}`;
    }
    return track?.name || "未命名轨";
}

/**
 * 构造外部参考轨在工作台内使用的稳定 key。
 * @param {Object} referenceSource - 外部来源挂载结果。
 * @returns {string} external 前缀的轨 key。
 */
export function buildExternalTrackKey(referenceSource) {
    return `${EXTERNAL_TRACK_KEY_PREFIX}:${referenceSource.reference_id || referenceSource.source_id || referenceSource.source_label}`;
}

/**
 * 构造 ScheduleDraftTrack 在工作台内使用的稳定 key。
 * @param {string|number} trackId - ScheduleDraftTrack ID。
 * @returns {string} track 前缀的轨 key。
 */
export function buildScheduleTrackKey(trackId) {
    return `${TRACK_KEY_PREFIX}:${trackId}`;
}

/**
 * 解析工作台轨 key 的类型和业务 ID。
 * @param {string} trackKey - draft-current、track:* 或 external:* 轨 key。
 * @returns {{type: string, id: string}} 解析后的轨身份。
 */
export function parseTrackKey(trackKey) {
    if (trackKey?.startsWith(`${TRACK_KEY_PREFIX}:`)) {
        return { type: TRACK_KEY_PREFIX, id: trackKey.replace(`${TRACK_KEY_PREFIX}:`, "") };
    }
    if (trackKey?.startsWith(`${EXTERNAL_TRACK_KEY_PREFIX}:`)) {
        return { type: EXTERNAL_TRACK_KEY_PREFIX, id: trackKey.replace(`${EXTERNAL_TRACK_KEY_PREFIX}:`, "") };
    }
    if (trackKey === DRAFT_CURRENT_TRACK_KEY) {
        return { type: "draft", id: "current" };
    }
    return { type: "unknown", id: trackKey };
}

/**
 * 判断轨 key 是否指向 ScheduleDraftTrack。
 * @param {string} trackKey - 待判断轨 key。
 * @returns {boolean} 是 ScheduleDraftTrack key 时返回 true。
 */
export function isScheduleTrackKey(trackKey) {
    return trackKey?.startsWith(`${TRACK_KEY_PREFIX}:`);
}

/**
 * 判断轨 key 是否指向外部参考轨。
 * @param {string} trackKey - 待判断轨 key。
 * @returns {boolean} 是外部参考轨时返回 true。
 */
export function isExternalTrackKey(trackKey) {
    return trackKey?.startsWith(`${EXTERNAL_TRACK_KEY_PREFIX}:`);
}

/**
 * 构建右侧轨配置面板使用的参考轨条目。
 * @param {string[]} referenceTrackKeys - 用户排序后的参考轨 key。
 * @param {Array<Object>} tracks - 草稿轨集合。
 * @param {Array<Object>} externalReferenceTracks - 已挂载外部参考轨集合。
 * @returns {Array<Object>} 可渲染的参考轨条目。
 */
export function buildReferenceTrackItems(referenceTrackKeys, tracks, externalReferenceTracks) {
    const tracksById = new Map(getReferenceDisplayTracks({ tracks }).map((track) => [String(track.id), track]));
    const externalByKey = new Map(toArray(externalReferenceTracks).map((track) => [track.trackKey, track]));

    return referenceTrackKeys
        .map((key, index) => {
            if (isScheduleTrackKey(key)) {
                const track = tracksById.get(parseTrackKey(key).id);
                if (!track) {
                    return null;
                }
                const agentTrack = isAgentTrack(track);

                return {
                    key,
                    sourceType: track.track_type,
                    label: getTrackDisplayName(track),
                    description: agentTrack ? getAgentEvaluationSummary(track) : track.summary || "只读参考轨",
                    flightCount: getTrackFlightCount(track),
                    isPrimary: index === 0,
                    track,
                };
            }

            const external = externalByKey.get(key);
            if (!external) {
                return null;
            }

            return {
                key,
                sourceType: external.source_type,
                label: external.source_label || external.reference_id,
                description: external.source_id ? `Source ID: ${external.source_id}` : "按当前筛选条件挂载的只读参考轨",
                flightCount: external.total || external.flights?.length || 0,
                isPrimary: index === 0,
                referenceSource: external,
            };
        })
        .filter(Boolean);
}
